import { useCallback, useState } from 'react'
import { createSelector } from '@reduxjs/toolkit'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import { STATES } from '../../../../../lib/routes'
import { $state } from '../../../ng-compat/imports'
import { RootState } from '../../../state/store'
import { useAppSelector } from '../../../state/hooks'
import {
  isCompleted,
  isConfirmedReadyToComplete,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isQuote,
} from '../../../functions/sale'
import { selectPayAccountBalance } from '../../../state/slices/currentSale/currentSaleSlice'

class InitError extends Error {
  public navigateTo: string

  constructor(message: string, navigateTo: string = STATES.SALE) {
    super(message)
    this.navigateTo = navigateTo
  }
}

const saleDataSelector = createSelector(
  [
    (state: RootState) => state.currentSale.status,
    (state: RootState) => state.currentSale.readyToComplete,
    (state: RootState) => state.currentSale.isSubmittedSaleEarly,
  ],
  (status, readyToComplete, isSubmittedSaleEarly) => ({
    status,
    readyToComplete,
    isSubmittedSaleEarly,
  })
)

export const useConfirmationInit = () => {
  const registerManager = useRegisterManager()
  const saleManager = useSaleManager()
  const saleData = useAppSelector(saleDataSelector)
  const payAccountBalance = useAppSelector(selectPayAccountBalance)
  const [hasInit, setHasInit] = useState(false)

  const runInit = useCallback(async () => {
    try {
      // Sale-less ABP: no carrier sale was created — skip saleManager.init() and
      // the status validation entirely. The confirmation panel's only job is to
      // present the Done button which triggers submitMonoliphForm + redirect.
      if (payAccountBalance?.abpTransactionId) {
        setHasInit(true)
        return
      }

      await registerManager.waitForSelectedRegister()

      if (!registerManager.isActiveRegisterOpen()) {
        throw new InitError('Register is closed', STATES.OPEN_REGISTER)
      }

      await saleManager.init()

      if (
        !isCompleted(saleData.status) &&
        !isConfirmedReadyToComplete(saleData) &&
        !isQuote(saleData.status) &&
        !isMarkedAsUnfulfilled(saleData.status) &&
        !isLegacyServiceSale(saleData.status)
      ) {
        throw new InitError('Sale is not ready to be closed')
      }
      setHasInit(true)
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Sale Confirmation Ctrl] Redirecting due to error loading confirmation screen:',
        e
      )
      const to = e instanceof InitError ? e.navigateTo : STATES.SALE
      $state.go(to, {}, { location: 'replace' })
    }
  }, [registerManager, saleData, saleManager, payAccountBalance])

  if (!hasInit) {
    runInit()
  }

  return hasInit
}
